"use client";
// 26차 후속 — 운영대시보드 위젯 1차 보정: 내 주요 로그인 (DB SoT).
// 보안 정책: ID/PW/토큰/쿠키/세션 일체 저장 금지. URL + 표시명 + memo 만.
// 본인(current_user) 링크만 조회/추가/수정/삭제.

import { useEffect, useState } from "react";
import {
  listMyExternalLinks,
  createMyExternalLink,
  updateMyExternalLink,
  deleteMyExternalLink,
  type MyExternalLink,
} from "@/lib/api";

function isValidUrl(v: string): boolean {
  const s = v.trim().toLowerCase();
  return s.startsWith("http://") || s.startsWith("https://");
}

export default function ExternalLinksCard() {
  const [links, setLinks] = useState<MyExternalLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 추가 폼
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newUrl, setNewUrl] = useState("");

  // 인라인 편집
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editUrl, setEditUrl] = useState("");

  async function reload() {
    setLoading(true);
    try {
      const rows = await listMyExternalLinks();
      setLinks(rows);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "조회 실패");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  async function handleCreate() {
    const title = newTitle.trim();
    const url = newUrl.trim();
    if (!title) {
      alert("제목을 입력하세요.");
      return;
    }
    if (!isValidUrl(url)) {
      alert("URL 은 http:// 또는 https:// 로 시작해야 합니다.");
      return;
    }
    try {
      await createMyExternalLink({ title, url });
      setNewTitle("");
      setNewUrl("");
      setAdding(false);
      await reload();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : "추가 실패");
    }
  }

  function startEdit(row: MyExternalLink) {
    setEditingId(row.id);
    setEditTitle(row.title);
    setEditUrl(row.url);
  }

  async function handleSaveEdit(id: number) {
    const title = editTitle.trim();
    const url = editUrl.trim();
    if (!title) {
      alert("제목을 입력하세요.");
      return;
    }
    if (!isValidUrl(url)) {
      alert("URL 은 http:// 또는 https:// 로 시작해야 합니다.");
      return;
    }
    try {
      await updateMyExternalLink(id, { title, url });
      setEditingId(null);
      await reload();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : "수정 실패");
    }
  }

  async function handleDelete(id: number) {
    if (!window.confirm("이 링크를 삭제할까요?")) return;
    try {
      await deleteMyExternalLink(id);
      await reload();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : "삭제 실패");
    }
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-bold text-gray-700">🔗 내 주요 로그인</p>
        <span className="text-[10px] text-gray-400">ID/PW 저장 안함 · 링크만</span>
      </div>

      {loading && <p className="text-xs text-gray-400">불러오는 중...</p>}
      {error && <p className="text-xs text-red-600">⚠️ {error}</p>}

      {!loading && (
        <ul className="space-y-1">
          {links.map((l) => (
            <li
              key={l.id}
              data-testid="external-link-row"
              className="flex items-center gap-2"
            >
              {editingId === l.id ? (
                <div className="flex-1 flex items-center gap-1">
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    placeholder="제목"
                    className="w-24 px-2 py-1 text-xs border border-gray-300 rounded"
                  />
                  <input
                    type="url"
                    value={editUrl}
                    onChange={(e) => setEditUrl(e.target.value)}
                    placeholder="https://..."
                    className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                  />
                  <button
                    type="button"
                    onClick={() => handleSaveEdit(l.id)}
                    className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded"
                  >
                    저장
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingId(null)}
                    className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded"
                  >
                    취소
                  </button>
                </div>
              ) : (
                <>
                  <a
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm font-semibold text-gray-800 transition-colors truncate"
                    title={l.url}
                  >
                    {l.title}
                    <span className="text-[10px] text-gray-400 ml-1">↗</span>
                  </a>
                  <button
                    type="button"
                    onClick={() => startEdit(l)}
                    className="px-2 py-1 text-[11px] text-gray-500 hover:text-gray-800 border border-gray-200 rounded"
                  >
                    수정
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(l.id)}
                    className="px-2 py-1 text-[11px] text-red-500 hover:text-red-700 border border-gray-200 rounded"
                  >
                    삭제
                  </button>
                </>
              )}
            </li>
          ))}
          {links.length === 0 && (
            <li className="text-xs text-gray-400 py-1">
              등록된 링크가 없습니다. 아래 "+ 링크 추가" 로 등록하세요.
            </li>
          )}
        </ul>
      )}

      {adding ? (
        <div className="mt-2 flex items-center gap-1">
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="제목"
            className="w-24 px-2 py-1 text-xs border border-gray-300 rounded"
          />
          <input
            type="url"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            placeholder="https://..."
            className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
          />
          <button
            type="button"
            onClick={handleCreate}
            className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded"
          >
            추가
          </button>
          <button
            type="button"
            onClick={() => {
              setAdding(false);
              setNewTitle("");
              setNewUrl("");
            }}
            className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded"
          >
            취소
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="mt-2 w-full text-xs px-2 py-1 border border-dashed border-gray-300 text-gray-500 rounded hover:bg-gray-50"
        >
          + 링크 추가
        </button>
      )}
    </div>
  );
}
